import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import clsx from "clsx";

import { makeRequest } from "../../utils/request";
import { Button, EmptyState, Spinner, Text, View } from "../../components/base";
import { Icon } from "../../components/base/Icon";
import { FineTuningPipelineStepper } from "../../components/FineTuningPipelineStepper";
import { FineTuningJobStatusPill } from "../../components/FineTuningJobStatusPill";

type ResultRecord = Record<string, unknown>;

type FineTuningEval = {
  id: string;
  status: string;
  phase: string | null;
  org_id: string;
  deployment_id: string;
  test_file: string;
  scorer: string;
  served_model_name: string;
  n_examples: number;
  metrics: Record<string, any>;
  results_uri: string | null;
  results_sample?: ResultRecord[] | null;
  error: string | null;
  created_at: string;
  finished_at: string | null;
};

type EvalEvent = {
  id: string;
  type: string;
  level: string;
  message: string;
  created_at: string;
};

const TERMINAL = new Set(["succeeded", "failed", "cancelled"]);
const POLL_MS = 6000;
// Platform-admin view across every org (the page is admin-only). Each row shows the owning org.
const ALL_ORGS = "all_orgs=true";

const COLUMNS = "1.3fr 1.4fr 1.2fr 1.2fr 0.9fr 0.7fr 1fr";

const asUtc = (iso: string) => (/[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : `${iso}Z`);

const relativeTime = (iso: string | null): string => {
  if (!iso) return "—";
  const ms = Date.now() - new Date(asUtc(iso)).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const shortId = (id: string) => (id.length > 16 ? `${id.slice(0, 14)}…` : id);

const pct = (v: unknown) => (typeof v === "number" ? `${(v * 100).toFixed(1)}%` : "—");
const fmt = (v: unknown) => (typeof v === "number" ? (Number.isInteger(v) ? String(v) : v.toFixed(3)) : String(v));

// the headline number shown in the list — pass_rate, or intent accuracy for the slot/intent scorer
const headline = (ev: FineTuningEval): string => {
  const m = ev.metrics || {};
  if (typeof m.pass_rate === "number") return pct(m.pass_rate);
  const inner = m.metrics || {};
  if (typeof inner.intent_accuracy === "number") return pct(inner.intent_accuracy);
  if (typeof inner.accuracy === "number") return pct(inner.accuracy);
  return "—";
};

export const AdminFineTuningEvalsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get("id");

  const [evals, setEvals] = useState<FineTuningEval[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadEvals = useCallback(async (showSpinner = false) => {
    if (showSpinner) setIsLoading(true);
    try {
      const { data } = await makeRequest<{ data: FineTuningEval[] }>(`/v1/fine_tuning/evaluations?${ALL_ORGS}&limit=200`);
      setEvals(data);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load evaluations");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadEvals(true);
  }, [loadEvals]);

  const hasActive = evals.some((e) => !TERMINAL.has(e.status));
  useEffect(() => {
    if (!hasActive) return;
    const t = setInterval(() => loadEvals(false), POLL_MS);
    return () => clearInterval(t);
  }, [hasActive, loadEvals]);

  const selected = evals.find((e) => e.id === selectedId) || null;

  return (
    <View className="h-full w-full bg-gray-50 dark:bg-slate-900">
      <div className={clsx("h-full w-full relative transition-all duration-300", selectedId && "pr-[440px]")}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Text className="text-xl font-semibold">Fine-Tuning Evals</Text>
            <Text className="text-sm text-gray-500 dark:text-gray-400">
              {evals.length} {evals.length === 1 ? "run" : "runs"} · all orgs
            </Text>
            {hasActive && <Spinner className="!h-4 !w-4 !text-brand" />}
          </div>
          <Button
            variant="ghost"
            type="button"
            onClick={() => loadEvals(true)}
          >
            <Icon
              name="refresh"
              size={18}
              className="text-brand"
            />
          </Button>
        </div>

        <FineTuningPipelineStepper />

        <div
          className="grid border-b-2 border-gray-200 dark:border-gray-700 px-6"
          style={{ gridTemplateColumns: COLUMNS }}
        >
          {["Org", "Eval", "Scorer", "Model", "Status", "Result", "Created"].map((h) => (
            <span
              key={h}
              className="py-3 pr-2 text-sm font-semibold text-gray-700 dark:text-gray-200"
            >
              {h}
            </span>
          ))}
        </div>

        <div
          className="overflow-y-auto"
          style={{ height: "calc(100% - 110px)" }}
        >
          {isLoading ? (
            <View className="flex items-center justify-center py-20">
              <Spinner className="!text-brand" />
            </View>
          ) : error ? (
            <View className="flex items-center justify-center py-20">
              <Text className="text-error">{error}</Text>
            </View>
          ) : evals.length === 0 ? (
            <EmptyState
              icon="checklist"
              title="No evaluations yet"
              description="Runs created via `vr ft eval` will appear here, across all organizations."
            />
          ) : (
            evals.map((ev) => (
              <div
                key={ev.id}
                onClick={() => setSearchParams({ id: ev.id })}
                className={clsx(
                  "grid items-center px-6 border-b border-gray-100 dark:border-gray-800 cursor-pointer",
                  "hover:bg-gray-100 dark:hover:bg-gray-800",
                  selectedId === ev.id && "bg-gray-100 dark:bg-gray-800",
                )}
                style={{ gridTemplateColumns: COLUMNS }}
              >
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={ev.org_id}
                >
                  {ev.org_id}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm font-mono"
                  title={ev.id}
                >
                  {shortId(ev.id)}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm"
                  title={ev.scorer}
                >
                  {ev.scorer}
                </span>
                <span
                  className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400"
                  title={ev.served_model_name}
                >
                  {ev.served_model_name || "—"}
                </span>
                <span className="py-3 pr-2">
                  <FineTuningJobStatusPill
                    status={ev.status}
                    phase={null}
                  />
                </span>
                <span className="py-3 pr-2 truncate text-sm font-medium">{headline(ev)}</span>
                <span className="py-3 pr-2 truncate text-sm text-gray-500 dark:text-gray-400">{relativeTime(ev.created_at)}</span>
              </div>
            ))
          )}
        </div>
      </div>

      {selected && (
        <EvalDetailPanel
          summary={selected}
          onClose={() => setSearchParams({})}
          onChanged={() => loadEvals(false)}
        />
      )}
    </View>
  );
};

type EvalDetailPanelProps = {
  summary: FineTuningEval;
  onClose: () => void;
  onChanged: () => void;
};

const EvalDetailPanel = ({ summary, onClose, onChanged }: EvalDetailPanelProps) => {
  // the list row lacks results_sample; fetch the full detail (and poll while active)
  const [ev, setEv] = useState<FineTuningEval>(summary);
  const [events, setEvents] = useState<EvalEvent[]>([]);
  const [isCancelling, setIsCancelling] = useState(false);
  const idRef = useRef(summary.id);
  idRef.current = summary.id;

  const isTerminal = TERMINAL.has(ev.status);

  const load = useCallback(async () => {
    try {
      const [detail, eventsRes] = await Promise.all([
        makeRequest<FineTuningEval>(`/v1/fine_tuning/evaluations/${summary.id}?all_orgs=true`),
        makeRequest<{ data: EvalEvent[] }>(`/v1/fine_tuning/evaluations/${summary.id}/events?all_orgs=true`),
      ]);
      if (idRef.current !== summary.id) return; // stale after switching rows
      setEv(detail);
      setEvents(eventsRes.data);
    } catch {
      /* best-effort */
    }
  }, [summary.id]);

  useEffect(() => {
    setEv(summary);
    setEvents([]);
    load();
  }, [summary, load]);

  useEffect(() => {
    if (TERMINAL.has(ev.status)) return;
    const t = setInterval(load, POLL_MS);
    return () => clearInterval(t);
  }, [ev.status, load]);

  const onCancel = async () => {
    setIsCancelling(true);
    try {
      await makeRequest(`/v1/fine_tuning/evaluations/${ev.id}?all_orgs=true`, { method: "DELETE" });
      onChanged();
      load();
    } catch {
      /* surfaced via refresh */
    } finally {
      setIsCancelling(false);
    }
  };

  const m = ev.metrics || {};
  const inner: Record<string, any> = m.metrics || {};
  const targets: Record<string, number> = m.targets || {};
  const latency = m.latency_ms || {};
  const confusion: Record<string, number> = inner.confusion || {};
  const sample = ev.results_sample || [];

  // scalar metrics (skip nested objects like confusion / per_class — shown separately)
  const scalarMetrics = Object.entries(inner).filter(([, v]) => typeof v !== "object");

  return (
    <div className="fixed right-0 top-0 h-screen w-[440px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col z-20">
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Text className="text-base font-semibold">Evaluation</Text>
          <FineTuningJobStatusPill
            status={ev.status}
            phase={null}
          />
        </div>
        <Button
          variant="ghost"
          type="button"
          onClick={onClose}
        >
          <Icon
            name="close"
            size={18}
            className="text-gray-500"
          />
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-5">
        <Section
          label="Eval ID"
          value={ev.id}
          mono
        />
        <Section
          label="Organization"
          value={ev.org_id}
          mono
        />
        <Field
          label="Scorer"
          value={ev.scorer}
        />
        <Field
          label="Served model"
          value={ev.served_model_name || "—"}
        />
        <Section
          label="Deployment"
          value={ev.deployment_id}
          mono
        />
        <Field
          label="Test file"
          value={ev.test_file}
          mono
        />
        <Field
          label="Examples"
          value={String(ev.n_examples || 0)}
        />
        <Field
          label="Created"
          value={new Date(asUtc(ev.created_at)).toLocaleString()}
        />
        {ev.finished_at && (
          <Field
            label="Finished"
            value={new Date(asUtc(ev.finished_at)).toLocaleString()}
          />
        )}
        {ev.error && (
          <div>
            <Text className="block text-xs uppercase text-gray-400 mb-1">Error</Text>
            <Text className="text-sm text-error whitespace-pre-wrap break-words">{ev.error}</Text>
          </div>
        )}

        {/* Metrics — value vs target, PASS/FAIL */}
        {scalarMetrics.length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">Metrics</Text>
            <div className="space-y-1">
              {typeof m.pass_rate === "number" && (
                <MetricRow
                  name="pass_rate"
                  value={pct(m.pass_rate)}
                />
              )}
              {scalarMetrics.map(([k, v]) => {
                const tgt = targets[k];
                const ok = typeof tgt === "number" && typeof v === "number" ? v >= tgt : undefined;
                return (
                  <MetricRow
                    key={k}
                    name={k}
                    value={fmt(v)}
                    target={typeof tgt === "number" ? tgt.toFixed(2) : undefined}
                    pass={ok}
                  />
                );
              })}
            </div>
            {(latency.p50 != null || latency.p95 != null) && (
              <Text className="block text-xs text-gray-400 mt-2">
                latency p50 {latency.p50}ms · p95 {latency.p95}ms · errors {m.errors ?? 0}
              </Text>
            )}
          </div>
        )}

        {/* Confusion matrix (gold → pred mistakes) */}
        {Object.keys(confusion).length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-1">Confusion (gold → pred)</Text>
            <div className="space-y-0.5">
              {Object.entries(confusion).map(([k, v]) => (
                <div
                  key={k}
                  className="flex justify-between text-xs font-mono"
                >
                  <span className="text-gray-600 dark:text-gray-300 break-all">{k}</span>
                  <span className="text-error">{v}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Per-example sample (failures first) */}
        {sample.length > 0 && (
          <div>
            <Text className="text-xs uppercase text-gray-400 mb-2">
              Examples ({sample.length} shown{ev.results_uri ? ", failures first" : ""})
            </Text>
            <div className="space-y-2">
              {sample.map((r, i) => (
                <ExampleRow
                  key={i}
                  r={r}
                />
              ))}
            </div>
          </div>
        )}

        {/* Events */}
        <div>
          <Text className="text-xs uppercase text-gray-400 mb-2">Events</Text>
          {events.length === 0 ? (
            <Text className="text-sm text-gray-400">No events yet.</Text>
          ) : (
            <div className="space-y-2">
              {events.map((e) => (
                <div
                  key={e.id}
                  className="flex gap-2 text-sm"
                >
                  <span
                    className={clsx(
                      "mt-1.5 h-1.5 w-1.5 rounded-full flex-shrink-0",
                      e.level === "error" ? "bg-error" : e.level === "warning" ? "bg-yellow-500" : "bg-info",
                    )}
                  />
                  <div className="min-w-0">
                    <Text className="block text-gray-800 dark:text-gray-200 break-words">{e.message}</Text>
                    <Text className="block text-xs text-gray-400 mt-0.5">{relativeTime(e.created_at)}</Text>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {!isTerminal && (
        <div className="p-4 border-t border-gray-200 dark:border-gray-700">
          <Button
            className="w-full"
            variant="ghost"
            type="button"
            onClick={onCancel}
            isLoading={isCancelling}
          >
            <Text className="text-error">Cancel eval</Text>
          </Button>
        </div>
      )}
    </div>
  );
};

const MetricRow = ({ name, value, target, pass }: { name: string; value: string; target?: string; pass?: boolean }) => (
  <div className="flex items-center justify-between gap-2 text-sm">
    <Text className="text-gray-600 dark:text-gray-300 font-mono text-xs">{name}</Text>
    <div className="flex items-center gap-2">
      <Text className="font-medium">{value}</Text>
      {target != null && <Text className="text-xs text-gray-400">/ {target}</Text>}
      {pass !== undefined && <Text className={clsx("text-xs font-semibold", pass ? "text-success" : "text-error")}>{pass ? "PASS" : "FAIL"}</Text>}
    </div>
  </div>
);

const ExampleRow = ({ r }: { r: ResultRecord }) => {
  const passed = r.passed === true;
  return (
    <div className="rounded-md border border-gray-100 dark:border-gray-800 p-2 text-xs">
      <div className="flex items-start gap-2">
        <span className={clsx("mt-0.5 font-bold flex-shrink-0", passed ? "text-success" : "text-error")}>{passed ? "✓" : "✗"}</span>
        <div className="min-w-0 space-y-0.5">
          <div className="break-words">
            <span className="text-gray-400">expected </span>
            <span className="font-mono">{String(r.expected ?? "")}</span>
          </div>
          <div className="break-words">
            <span className="text-gray-400">actual </span>
            <span className="font-mono">{String(r.actual ?? "")}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div className="flex items-baseline justify-between gap-3">
    <Text className="text-xs uppercase text-gray-400 flex-shrink-0">{label}</Text>
    <Text className={clsx("text-sm text-right break-words", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);

const Section = ({ label, value, mono }: { label: string; value: string; mono?: boolean }) => (
  <div>
    {/* the design-system Text is inline; `block` keeps the value on its own line under the label */}
    <Text className="block text-xs uppercase text-gray-400 mb-1">{label}</Text>
    <Text className={clsx("block text-sm break-all", mono && "font-mono text-xs")}>{value}</Text>
  </div>
);
